import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Callout } from '@/shared/ui/Callout';
import { usePracticeFeatureFlags } from '@/shared/hooks/usePracticeFeatureFlags';
import {
  reviewSmsApi,
  type ProgramSettingsInput,
  type ProgramView,
  type TemplateValidation,
} from '../api/reviewSmsApi';
import { isTestInProgress, type useSmsProgram } from './hooks';
import {
  isLive,
  stepsDone,
  textingReadiness,
  PRACTICE_TIME_ZONES,
  timeZoneName,
  VISIT_MAX_AGE_DAYS,
} from './format';
import styles from './TextRequests.module.css';

type Program = ReturnType<typeof useSmsProgram>;

/** How long "Send test" rests after a send, so a double tap never fires a second test. */
const TEST_REST_MS = 5_000;

// The texting number and carrier registration are set up once per location in Settings → Communication → Texting
// (shared by every texting feature); this wizard shows where they stand (TextingStatus).
const STEPS = [
  { title: 'When texts go out', hint: 'Timing and quiet hours' },
  { title: 'What the texts say', hint: 'Voice, questions, unhappy patients' },
  { title: 'Daily routine', hint: 'Appointments in, visits confirmed' },
  { title: 'Test & go live', hint: 'Send yourself a test' },
];

function Switch({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      className={clsx(styles.switch, on && styles.switchOn)}
      onClick={() => onChange(!on)}
    />
  );
}

function Toggle({
  title,
  hint,
  on,
  onChange,
  disabled,
}: {
  title: string;
  hint: string;
  on: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className={styles.toggle}>
      <div className={styles.toggleText}>
        <span className={styles.toggleTitle}>{title}</span>
        <span className={styles.hint}>{hint}</span>
      </div>
      <Switch on={on} onChange={onChange} label={title} disabled={disabled} />
    </div>
  );
}

function Footer({
  step,
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  loading,
}: {
  step: number;
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
}) {
  return (
    <div className={styles.wizardFooter}>
      <Button variant="ghost" size="sm" disabled={step === 0} onClick={onBack}>
        ← Back
      </Button>
      <Button size="sm" onClick={onNext} disabled={nextDisabled} isLoading={loading}>
        {nextLabel ?? 'Save & continue'}
      </Button>
    </div>
  );
}

// ── Shared: a checklist row ──────────────────────────────────────────────────

function Check({
  state,
  title,
  hint,
  right,
}: {
  state: 'done' | 'wait' | 'todo';
  title: string;
  hint?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className={styles.checkItem}>
      <span
        className={clsx(
          styles.tick,
          state === 'done' && styles.tickDone,
          state === 'wait' && styles.tickWait,
        )}
      >
        {state === 'done' ? '✓' : state === 'wait' ? '…' : ''}
      </span>
      <div className={styles.checkText}>
        <span className={styles.checkTitle}>{title}</span>
        {hint && <span className={styles.hint}>{hint}</span>}
      </div>
      {right}
    </div>
  );
}

// ── Step 1: timing ───────────────────────────────────────────────────────────

function hourOf(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h + (m || 0) / 60;
}

function Timeline({
  delayMinutes,
  windowStart,
  windowEnd,
}: {
  delayMinutes: number;
  windowStart: string;
  windowEnd: string;
}) {
  const ws = hourOf(windowStart);
  const we = hourOf(windowEnd);
  const visitEnd = 11.83;
  const send = Math.max(visitEnd + delayMinutes / 60, ws);
  const cells = Array.from({ length: 24 }, (_, h) => {
    if (h === Math.floor(send)) return styles.timelineSend;
    if (h >= 10.5 && h < 12) return styles.timelineVisit;
    if (h >= ws - 0.01 && h < we) return styles.timelineWindow;
    return styles.timelineCell;
  });
  const hh = Math.floor(send);
  const mm = Math.round((send - hh) * 60);
  const label = `${((hh + 11) % 12) + 1}:${mm < 10 ? '0' : ''}${mm} ${hh < 12 ? 'AM' : 'PM'}`;
  return (
    <div>
      <div className={styles.timeline}>
        {cells.map((cls, i) => (
          <span key={i} className={cls} />
        ))}
      </div>
      <div className={styles.timelineLabels}>
        <span>12 AM</span>
        <span>6 AM</span>
        <span>12 PM</span>
        <span>6 PM</span>
        <span>12 AM</span>
      </div>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <i className={clsx(styles.legendSwatch, styles.timelineVisit)} /> 10:30–11:50 AM visit
        </span>
        <span className={styles.legendItem}>
          <i className={clsx(styles.legendSwatch, styles.timelineWindow)} /> Sending window
        </span>
        <span className={styles.legendItem}>
          <i className={clsx(styles.legendSwatch, styles.timelineSend)} /> Text goes out ≈ {label}
        </span>
      </div>
    </div>
  );
}

/** Betty's running test of this kind, said where an edit would end it. */
function RunningTest({
  program,
  kind,
  ends,
}: {
  program: Program;
  kind: 'timing' | 'template';
  ends: string;
}) {
  const test = program.data?.tests?.find((t) => t.kind === kind);
  if (!test) return null;
  const until = new Date(test.endsAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return (
    <Callout variant="info" title={`Betty is testing this until ${until}`}>
      {test.description}. Half of new texts try it; she reports the result in Tasks. {ends}
    </Callout>
  );
}

function TimingStep({
  program,
  canEdit,
  onBack,
  onNext,
}: {
  program: Program;
  canEdit: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const t = program.data!.timing;
  const [delay, setDelay] = useState(t.delayMinutes);
  const [start, setStart] = useState(t.windowStartLocal);
  const [end, setEnd] = useState(t.windowEndLocal);
  const [sundays, setSundays] = useState(t.sendOnSundays);
  const [reminder, setReminder] = useState(t.reminderEnabled);
  const [tuning, setTuning] = useState(t.bettyTuningEnabled);
  const tz = t.timezone;
  const [zone, setZone] = useState(t.practiceTimezone);
  const zones = [
    ...new Set([
      t.practiceTimezone,
      ...(tz.addressZone ? [tz.addressZone] : []),
      ...PRACTICE_TIME_ZONES,
    ]),
  ];
  const zoneSource =
    zone !== t.practiceTimezone || tz.source === 'texts_setting'
      ? 'Set here for texts. It takes precedence over your location settings.'
      : tz.source === 'address'
        ? 'From your practice’s address. Change it here if texts should use a different zone.'
        : 'From your location settings (we couldn’t place the address). Check it’s right.';

  const save = () =>
    program.update.mutate(
      {
        ...(zone !== t.practiceTimezone ? { textTimezone: zone } : {}),
        delayMinutes: delay,
        windowStartLocal: start,
        windowEndLocal: end,
        sendOnSundays: sundays,
        reminderEnabled: reminder,
        bettyTuningEnabled: tuning,
      },
      { onSuccess: onNext },
    );

  return (
    <div className={styles.stepBody}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepHeading}>When texts go out</h3>
      </div>
      <RunningTest
        program={program}
        kind="timing"
        ends="Changing these times, or turning her testing off, ends the test."
      />
      <div className={styles.row}>
        <Select
          label="Time zone for texts"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          disabled={!canEdit}
          options={zones.map((z) => ({
            value: z,
            label: `${timeZoneName(z)} (${z})${z === tz.addressZone ? ' · your address' : ''}`,
          }))}
          hint={`Visit times, the sending window and quiet hours all use it. ${zoneSource}`}
        />
      </div>
      {tz.conflicts.length > 0 && (
        <Callout variant="warning" title="Time zones don’t match">
          Texts use {timeZoneName(t.practiceTimezone)}
          {tz.source === 'texts_setting' ? ' (set here)' : ' (from your address)'}.{' '}
          {tz.conflicts
            .map((c) =>
              c.kind === 'address'
                ? `Your practice’s address is in ${timeZoneName(c.zone)}.`
                : `Your location settings say ${timeZoneName(c.zone)}.`,
            )
            .join(' ')}{' '}
          The time zone set here always wins for texts. If the location setting is wrong, change it
          in <Link to="/settings/practice">Settings → Practice & Locations</Link> so other features
          match.
        </Callout>
      )}
      <div className={styles.row}>
        <Select
          label="Wait after the visit ends"
          value={String(delay)}
          onChange={(e) => setDelay(Number(e.target.value))}
          disabled={!canEdit}
          options={[60, 120, 180, 240].map((m) => ({
            value: String(m),
            label: `${m / 60} hour${m > 60 ? 's' : ''}`,
          }))}
          hint="Long enough that they’re home, off your Wi-Fi."
        />
        <Input
          label="Earliest text"
          type="time"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          disabled={!canEdit}
          hint={`Hard limit ${t.hardLimits.start}`}
        />
        <Input
          label="Latest text"
          type="time"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          disabled={!canEdit}
          hint={`Hard limit ${t.hardLimits.end}`}
        />
      </div>
      <div>
        <div className={styles.hint}>A typical morning visit</div>
        <Timeline delayMinutes={delay} windowStart={start} windowEnd={end} />
      </div>
      <div>
        <Toggle
          title="Send on Sundays"
          hint="Off: Saturday-evening and Sunday texts move to Monday 12:30 PM."
          on={sundays}
          onChange={setSundays}
          disabled={!canEdit}
        />
        <Toggle
          title="One reminder if there’s no reply"
          hint="Sent once, 2 days later, in the same window. Never more than one."
          on={reminder}
          onChange={setReminder}
          disabled={!canEdit}
        />
        <Toggle
          title="Let Betty test send times and wording"
          hint="She proposes one change at a time in Tasks. Nothing changes until you approve it: then half of new texts try it for two weeks and she reports back. Off: she stops proposing, and any test running ends."
          on={tuning}
          onChange={setTuning}
          disabled={!canEdit}
        />
      </div>
      <p className={styles.hint}>
        Times use the patient’s time zone when your roster has one, otherwise the one above (
        {timeZoneName(zone)}). A visit confirmed later is texted soon after you confirm it, up to{' '}
        {VISIT_MAX_AGE_DAYS} days after the visit; if quiet hours or Sundays would push a text more
        than {t.staleCutoffHours} hours past that, we skip it rather than send a stale request.
      </p>
      <Footer
        step={0}
        onBack={onBack}
        onNext={canEdit ? save : onNext}
        nextLabel={canEdit ? undefined : 'Continue'}
        loading={program.update.isPending}
      />
    </div>
  );
}

// ── Step 2: messages ─────────────────────────────────────────────────────────

// visit_when reads "today", "yesterday" or "on Monday", from when the text actually goes out.
const MERGE_FIELDS = ['first_name', 'provider', 'practice', 'visit_when'] as const;

function MessagesStep({
  program,
  canEdit,
  onBack,
  onNext,
}: {
  program: Program;
  canEdit: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const view = program.data!;
  const [firstText, setFirstText] = useState(view.messages.firstText);
  const [reminderText, setReminderText] = useState(view.messages.reminderText);
  const [mentionProvider, setMentionProvider] = useState(view.messages.mentionProvider);
  const [check, setCheck] = useState<TemplateValidation | null>(null);
  const [checkTimer, setCheckTimer] = useState<number | null>(null);

  useEffect(() => {
    if (checkTimer) window.clearTimeout(checkTimer);
    const id = window.setTimeout(() => {
      reviewSmsApi
        .validateTemplate(program.locationId, 'first_text', firstText)
        .then(setCheck)
        .catch(() => setCheck(null));
    }, 400);
    setCheckTimer(id);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstText, program.locationId]);

  const insert = (field: (typeof MERGE_FIELDS)[number]) => setFirstText((t) => `${t} <${field}>`);
  // Only what changed: re-sending an untouched text would version it (and end a running test).
  const save = () => {
    const input: ProgramSettingsInput = {};
    if (firstText.trim() !== view.messages.firstText.trim()) input.firstText = firstText.trim();
    if (reminderText.trim() !== view.messages.reminderText.trim())
      input.reminderText = reminderText.trim();
    if (mentionProvider !== view.messages.mentionProvider) input.mentionProvider = mentionProvider;
    if (Object.keys(input).length === 0) onNext();
    else program.update.mutate(input, { onSuccess: onNext });
  };

  return (
    <div className={styles.stepBody}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepHeading}>What the texts say</h3>
        <span className={styles.hint}>Written in your practice’s voice</span>
      </div>
      <RunningTest
        program={program}
        kind="template"
        ends="Editing the message being tested ends the test."
      />
      <label className={styles.hint} htmlFor="sms-first-text">
        First text
      </label>
      <textarea
        id="sms-first-text"
        className={styles.textarea}
        value={firstText}
        onChange={(e) => setFirstText(e.target.value)}
        disabled={!canEdit}
      />
      <div className={styles.mergeRow}>
        Insert:
        {MERGE_FIELDS.map((f) => (
          <button
            key={f}
            type="button"
            className={styles.mergeChip}
            onClick={() => insert(f)}
            disabled={!canEdit}
          >
            {f.replace('_', ' ')}
          </button>
        ))}
        {check && (
          <span>
            · {check.characters} characters ({check.segments} text{check.segments === 1 ? '' : 's'})
            {check.gsm7 ? '' : ' · special characters make this longer'}
          </span>
        )}
      </div>
      {check && check.errors.length > 0 && (
        <Callout variant="error">{check.errors.join(' ')}</Callout>
      )}
      {check && check.warnings.length > 0 && (
        <Callout variant="warning">{check.warnings.join(' ')}</Callout>
      )}
      <label className={styles.hint} htmlFor="sms-reminder-text">
        Reminder (sent once if there is no reply)
      </label>
      <textarea
        id="sms-reminder-text"
        className={styles.textarea}
        value={reminderText}
        onChange={(e) => setReminderText(e.target.value)}
        disabled={!canEdit}
      />
      <div>
        <Toggle
          title="Mention the provider by name"
          hint="Never the procedure. Texts leave out anything about treatment."
          on={mentionProvider}
          onChange={setMentionProvider}
          disabled={!canEdit}
        />
        <div className={styles.toggle}>
          <div className={styles.toggleText}>
            <span className={styles.toggleTitle}>Questions before the draft</span>
            <span className={styles.hint}>
              “What stood out?”, one follow-up, and who to thank. Same set as your QR flow (Reviews
              → Settings).
            </span>
          </div>
        </div>
        <div className={styles.toggle}>
          <div className={styles.toggleText}>
            <span className={styles.toggleTitle}>Anything below 5 stars</span>
            <span className={styles.hint}>
              Ratings of 1–4: ask what would have made it better, alert your office manager, and
              still offer the public review link. Only 5★ gets a drafted review, same as your QR
              flow. Alerts go to every owner and admin by email.
            </span>
          </div>
        </div>
      </div>
      <Callout variant="accent">
        Betty keeps drafts to what the patient actually said. The patient approves every word and
        posts from their own Google account. We never offer rewards for reviews.
      </Callout>
      <Footer
        step={1}
        onBack={onBack}
        onNext={canEdit ? save : onNext}
        nextDisabled={canEdit && check !== null && !check.ok}
        nextLabel={canEdit ? undefined : 'Continue'}
        loading={program.update.isPending}
      />
    </div>
  );
}

// ── Step 3: routine ──────────────────────────────────────────────────────────

function RoutineStep({
  program,
  canEdit,
  onBack,
  onNext,
}: {
  program: Program;
  canEdit: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const r = program.data!.routine;
  const [eod, setEod] = useState(r.eodReminderEnabled);
  const [eodTime, setEodTime] = useState(r.eodReminderLocal);

  const save = () =>
    program.update.mutate(
      { eodReminderEnabled: eod, eodReminderLocal: eodTime },
      { onSuccess: onNext },
    );
  const recipients = r.eodRecipients
    .map((p) => (p.name ? `${p.name} (${p.emailMasked})` : p.emailMasked))
    .join(', ');

  return (
    <div className={styles.stepBody}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepHeading}>Your daily routine</h3>
        <span className={styles.hint}>About 2 minutes a day for the front desk</span>
      </div>
      <div className={styles.checkList}>
        <Check
          state="done"
          title="Before the day starts: upload the schedule (best)"
          hint="Export from your PMS and drop it on the Today tab, or paste the rows. Tomorrow’s or next week’s schedule works too."
        />
        <Check
          state="done"
          title="As visits finish: mark them completed"
          hint="One tap per patient, or “mark all finished” at the end of the day. No-shows and cancellations never get a text."
        />
        <Check
          state="done"
          title="Busy day? Catch up later"
          hint={`Uploading and confirming at the end of the day, or a few days later, still works: Betty texts shortly after you confirm. Visits more than ${VISIT_MAX_AGE_DAYS} days old aren’t accepted, and the sooner the text, the more patients reply.`}
        />
        <Check
          state="done"
          title="Betty takes it from there"
          hint="She times each text, runs the conversation, and flags anything you need to see."
        />
      </div>
      <Toggle
        title="End-of-day reminder to confirm visits"
        hint="If any of today’s finished visits aren’t confirmed by the reminder time, your team gets an email with a link to the Today tab, where one tap confirms them all."
        on={eod}
        onChange={setEod}
        disabled={!canEdit}
      />
      <div className={styles.row}>
        <Input
          label="Reminder time"
          type="time"
          value={eodTime}
          onChange={(e) => setEodTime(e.target.value)}
          disabled={!canEdit}
        />
      </div>
      <p className={styles.hint}>
        {recipients
          ? `Emails go to ${recipients}. They go to your team’s Managers and Viewers (Settings → Team & Staff), or to owners and admins if there are none.`
          : 'Nobody to email yet: invite your front desk in Settings → Team & Staff.'}
      </p>
      <p className={styles.hint}>
        Coming later: automatic sync with Open Dental and Dentrix so there’s nothing to upload or
        confirm.
      </p>
      <Footer
        step={2}
        onBack={onBack}
        onNext={canEdit ? save : onNext}
        nextLabel={canEdit ? undefined : 'Continue'}
        loading={program.update.isPending}
      />
    </div>
  );
}

// ── Step 4: test & go live ───────────────────────────────────────────────────

/** Where the texting number and carrier registration stand (set up in Settings → Communication → Texting). */
function TextingStatus({ view }: { view: ProgramView }) {
  const r = textingReadiness(view);
  if (r.ready) return null;
  return (
    <Callout
      variant={r.numberLive ? 'info' : 'warning'}
      title={
        r.numberLive
          ? 'Waiting on carrier registration (10DLC)'
          : 'Set up your texting number first'
      }
      actions={
        <Link to="/settings/communication" className={styles.linkButton}>
          Open Settings → Communication → Texting
        </Link>
      }
    >
      {r.numberLive
        ? `Your number ${view.sendingNumber.masked ?? ''} is live. ${
            view.registration.submittedAt
              ? 'The brand and campaign are with the carriers; we check every 3 hours.'
              : 'Send the carrier registration from Settings → Communication → Texting.'
          } Nothing texts patients, tests included, until both are approved. You can finish the steps below meanwhile.`
        : view.sendingNumber.provisioningState === 'requested'
          ? `PracticeNova is setting up ${view.sendingNumber.masked ?? 'your number'}. Carrier registration opens once it’s live. You can finish the steps below meanwhile.`
          : 'Pick a texting number in Settings → Communication → Texting. Then the carrier registration. You can finish the steps below meanwhile.'}
    </Callout>
  );
}

function TestStep({
  program,
  canEdit,
  onBack,
}: {
  program: Program;
  canEdit: boolean;
  onBack: () => void;
}) {
  const view = program.data!;
  const [phone, setPhone] = useState('');
  // A test on this phone is partway through: ask before ending it.
  const [confirmReplace, setConfirmReplace] = useState(false);
  // A few seconds after a send the button rests, so a double tap never fires a second test.
  const [resting, setResting] = useState(false);
  const sendTest = (replace: boolean) => {
    setConfirmReplace(false);
    program.sendTest.mutate(
      { phone: phone.trim(), replace },
      {
        onSuccess: () => {
          setResting(true);
          window.setTimeout(() => setResting(false), TEST_REST_MS);
        },
        onError: (err) => setConfirmReplace(isTestInProgress(err)),
      },
    );
  };
  const done = stepsDone(view);
  const ready = textingReadiness(view);
  // Test mode marks the program live so the test can text; patients are texted only after Go live.
  const live = isLive(view);
  // The backend (enableTestMode) allows tests only for internal practices during the pilot, and
  // never while the program is paused; say so here instead of letting Send test fail.
  const { flags, isLoading: flagsLoading } = usePracticeFeatureFlags();
  const internal = flags?.internalPractice === true;
  const paused = view.status === 'paused';
  const canTest = canEdit && ready.ready && internal && !paused;

  return (
    <div className={styles.stepBody}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepHeading}>Test &amp; go live</h3>
        {view.test.enabled && (
          <span className={styles.pillInfo}>Test mode · {view.test.phoneMasked}</span>
        )}
      </div>
      {!ready.ready && (
        <Callout variant="warning" title="Tests are off until texting is approved">
          {ready.numberLive
            ? 'Carriers haven’t approved the brand and campaign yet. Test texts start the moment they do.'
            : 'Your texting number isn’t live yet.'}{' '}
          <Link to="/settings/communication">See Settings → Communication → Texting</Link>.
        </Callout>
      )}
      {!flagsLoading && !internal && (
        <Callout variant="info" title="Test texts aren’t available yet">
          During the pilot, test texts are limited to PracticeNova’s own practices. Review the
          messages in the steps above, then go live when you’re ready.
        </Callout>
      )}
      {internal && paused && (
        <Callout variant="info" title="Texting is paused">
          Resume texting (top right) before sending a test.
        </Callout>
      )}
      <div className={styles.row}>
        <Input
          label="Send a test to"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={
            view.test.phoneMasked
              ? `Last test: ${view.test.phoneMasked}`
              : '(415) 555-0117 · your mobile'
          }
          hint="Betty runs the whole conversation with you: rating, questions, draft, link. Nothing counts in your numbers."
          disabled={!canTest}
        />
        <Button
          size="sm"
          isLoading={program.sendTest.isPending}
          disabled={!canTest || !phone.trim() || resting || confirmReplace}
          onClick={() => sendTest(false)}
        >
          Send test
        </Button>
      </div>
      {confirmReplace && (
        <Callout
          variant="warning"
          title="A test is in progress on this phone"
          actions={
            <>
              <Button size="sm" onClick={() => sendTest(true)}>
                Start over
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmReplace(false)}>
                Keep it
              </Button>
            </>
          }
        >
          You&apos;re partway through a test conversation with Betty. Starting over ends it and
          sends a new first text.
        </Callout>
      )}
      {view.test.enabled && (
        <Callout
          variant="info"
          title={`Test texts come from ${view.sendingNumber.masked ?? 'your number'}`}
          actions={
            canEdit ? (
              <Button
                variant="ghost"
                size="sm"
                isLoading={program.disableTest.isPending}
                onClick={() => program.disableTest.mutate()}
              >
                Turn test mode off
              </Button>
            ) : undefined
          }
        >
          While test mode is on, texts go only to {view.test.phoneMasked}. Turn it off before
          patients should start receiving requests.
        </Callout>
      )}
      <div className={styles.checkList}>
        <Check
          state={ready.numberLive ? 'done' : 'todo'}
          title="Texting number"
          hint={
            ready.numberLive
              ? (view.sendingNumber.masked ?? undefined)
              : 'Settings → Communication → Texting'
          }
        />
        <Check
          state={ready.ready ? 'done' : view.registration.submittedAt ? 'wait' : 'todo'}
          title="Carrier registration (10DLC)"
          hint={
            ready.ready
              ? 'Approved'
              : view.registration.submittedAt
                ? 'With the carriers. We check every 3 hours.'
                : 'Settings → Communication → Texting'
          }
          right={
            !ready.ready && view.registration.submittedAt ? (
              <span className={styles.pillSetup}>Pending</span>
            ) : undefined
          }
        />
        <Check state={done[0] ? 'done' : 'todo'} title="Timing" />
        <Check state={done[1] ? 'done' : 'todo'} title="Messages" />
        <Check state={done[2] ? 'done' : 'todo'} title="Daily routine" />
      </div>
      <p className={styles.hint}>
        Going live starts with today’s confirmed visits once registration is approved
        {view.test.enabled ? ' and turns test mode off' : ''}. You can pause everything from this
        tab at any time.
      </p>
      <div className={styles.wizardFooter}>
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back
        </Button>
        {live ? (
          // Already live: say so instead of showing a button with nothing left to do.
          <span className={styles.liveNote}>
            You’re live: Betty texts each patient after their confirmed visit.{' '}
            <Link to={{ search: '?tab=capture&sub=today' }}>Go to Today →</Link>
          </span>
        ) : (
          <Button
            size="sm"
            disabled={!canEdit || !ready.numberLive}
            isLoading={program.goLive.isPending}
            onClick={() => program.goLive.mutate()}
            title={!ready.numberLive ? 'Set up a texting number first' : undefined}
          >
            Go live
          </Button>
        )}
      </div>
    </div>
  );
}

// ── The wizard ───────────────────────────────────────────────────────────────

export function SetupWizard({ program, canEdit }: { program: Program; canEdit: boolean }) {
  const view = program.data as ProgramView;
  const done = useMemo(() => stepsDone(view), [view]);
  const last = STEPS.length - 1;
  const [step, setStep] = useState(() => {
    const first = done.findIndex((d) => !d);
    return first < 0 ? last : first;
  });
  const back = () => setStep((s) => Math.max(0, s - 1));
  const next = () => setStep((s) => Math.min(last, s + 1));
  const n = done.filter(Boolean).length;

  return (
    <div className={styles.wizardWrap}>
      <TextingStatus view={view} />
      <div className={styles.wizard}>
        <nav className={styles.steps} aria-label="Setup steps">
          {STEPS.map((s, i) => (
            <button
              key={s.title}
              type="button"
              className={clsx(styles.step, step === i && styles.stepActive)}
              onClick={() => setStep(i)}
              aria-current={step === i ? 'step' : undefined}
            >
              <span className={clsx(styles.stepNumber, done[i] && styles.stepNumberDone)}>
                {done[i] ? '✓' : i + 1}
              </span>
              <span className={styles.stepText}>
                <span className={styles.stepTitle}>{s.title}</span>
                <span className={styles.stepHint}>{s.hint}</span>
              </span>
              {done[i] && <span className={styles.pillLive}>Done</span>}
            </button>
          ))}
          <span className={styles.hint}>
            {n} of {STEPS.length} done
            {isLive(view) ? ' · live' : view.test.enabled ? ' · test mode' : ''}
          </span>
        </nav>
        {step === 0 && (
          <TimingStep
            key={`t-${view.timing.delayMinutes}`}
            program={program}
            canEdit={canEdit}
            onBack={back}
            onNext={next}
          />
        )}
        {step === 1 && (
          <MessagesStep
            key={`m-${view.messages.firstTextVersion}`}
            program={program}
            canEdit={canEdit}
            onBack={back}
            onNext={next}
          />
        )}
        {step === 2 && (
          <RoutineStep program={program} canEdit={canEdit} onBack={back} onNext={next} />
        )}
        {step === 3 && <TestStep program={program} canEdit={canEdit} onBack={back} />}
      </div>
    </div>
  );
}
